// AI credit helpers shared across the credit UI (R1 / OP#2257).
//
// Pricing is static per feature on the backend (no public quote endpoint yet),
// so the costs below mirror AiCreditService::COST_* constants. Keep them in sync
// if the server's pricing rules change. They drive the "explicit cost" labels and
// the confirm dialog for expensive operations (§14).

// Per-feature credit cost. calendar_generate is heavier and is *reserved*, not
// deducted up-front: the job settles the actual amount afterwards.
export const AI_FEATURE_COST = {
  post_generation: 1,
  hashtags: 1,
  calendar_analyze: 1,
  calendar_topics: 1,
  calendar_generate: 2,
};

// Operations at or above this cost get a confirm dialog before they run.
// Cheaper ones (1 credit) run without a dialog but still show the cost.
export const EXPENSIVE_CREDIT_THRESHOLD = 2;

/**
 * Credit cost for a feature, falling back to 1 for unknown features.
 * @param {string} feature - Feature key (e.g. "post_generation")
 * @returns {number} Cost in credits
 */
export function creditCostFor(feature) {
  return AI_FEATURE_COST[feature] ?? 1;
}

/**
 * Whether an operation is "expensive" enough to warrant a confirm dialog.
 * @param {number} cost - Cost in credits
 * @returns {boolean} True when the operation should be confirmed before running
 */
export function isExpensive(cost) {
  return Number(cost) >= EXPENSIVE_CREDIT_THRESHOLD;
}

/**
 * Extract the structured shortfall payload from an insufficient-credits (402)
 * response so the UI can render an inline upsell ("you need X, have Y").
 *
 * The server's error envelope (AbstractController::sendError) carries the
 * structured data in `error`/`message` and the human string in `data`, so we
 * probe all three and return the first object that looks like the shortfall.
 *
 * @param {Object} resp - Response from RequestAbstract (isInsufficientCredits)
 * @returns {{required:number, available:number, monthly_remaining:number,
 *   promotional_balance:number, purchased_balance:number}|null} The shortfall
 *   payload, or null when the response carries no structured data
 */
export function extractCreditShortfall(resp) {
  if (!resp) return null;
  for (const candidate of [resp.error, resp.message, resp.data]) {
    if (
      candidate &&
      typeof candidate === "object" &&
      ("required" in candidate || "available" in candidate)
    ) {
      return candidate;
    }
  }
  return null;
}

/**
 * The plan's configured monthly AI credit limit, read from the package's
 * `limits.ai_credits_monthly` (which the API may serialize as a JSON string).
 * Returns a number (−1 means unlimited) or null when the plan declares no AI.
 * @param {Object} pkg - metaData.subscription.package (current plan)
 * @returns {number|null} The configured monthly limit, or null when absent
 */
export function planAiCreditLimit(pkg) {
  let limits = pkg?.limits;
  if (typeof limits === "string") {
    try {
      limits = JSON.parse(limits);
    } catch {
      limits = null;
    }
  }
  const value = limits?.ai_credits_monthly;
  return value === undefined || value === null ? null : Number(value);
}

/**
 * Whether the current plan includes AI generation at all. This is derived from
 * the *plan* (its configured credit limit), never from the wallet's current
 * allowance — an allowance of 0 on a paid plan means "out of credits", not "no
 * AI in your plan", and the two must not be conflated in the UI.
 * @param {Object} pkg - metaData.subscription.package (current plan)
 * @returns {boolean} True when the plan grants AI credits (finite > 0 or unlimited)
 */
export function planIncludesAI(pkg) {
  const limit = planAiCreditLimit(pkg);
  return limit === -1 || (typeof limit === "number" && limit > 0);
}

/**
 * Whether the wallet is in a low-balance state. Prefers the backend's
 * `low_balance` flag (FR-052); falls back to a local 10%-of-allowance heuristic
 * when the summary predates the field.
 * @param {Object} aiCredits - ai_credits summary from metaData
 * @returns {boolean} True when the wallet is in a low-balance state
 */
export function isLowBalance(aiCredits) {
  if (!aiCredits) return false;
  if (typeof aiCredits.low_balance === "boolean") return aiCredits.low_balance;

  const remaining = aiCredits.monthly_remaining ?? 0;
  const allowance = aiCredits.monthly_allowance ?? 0;
  if (allowance <= 0) return false;
  return remaining <= Math.max(1, Math.floor(allowance * 0.1));
}
